import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, LogOut, Bot, User as UserIcon, Sparkles, MessageSquare, X } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const fetchSessions = async () => {
    try {
      const { data } = await api.get("/admin/chat/sessions");
      setSessions(data);
      if (!activeId && data.length > 0) setActiveId(data[0].id);
    } catch (e) {
      /* noop */
    }
  };

  const fetchMessages = async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/admin/chat/sessions/${id}`);
      setSession(data.session);
      setMessages(data.messages);
    } catch (e) {
      /* noop */
    }
  };

  useEffect(() => {
    fetchSessions();
    const iv = setInterval(fetchSessions, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchMessages(activeId);
    if (!activeId) return;
    const iv = setInterval(() => fetchMessages(activeId), 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendReply = async (e) => {
    e?.preventDefault();
    if (!text.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post(`/admin/chat/sessions/${activeId}/reply`, { text });
      setText("");
      await fetchMessages(activeId);
      await fetchSessions();
    } finally {
      setSending(false);
    }
  };

  const toggleAI = async () => {
    if (!session) return;
    await api.post(`/admin/chat/sessions/${session.id}/toggle-ai`, { ai_enabled: !session.ai_enabled });
    fetchMessages(session.id);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="flex h-screen flex-col bg-cream">
      {/* Top bar */}
      <header className="flex items-center justify-between border-b border-border bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center rounded-md bg-umber text-cream">S</span>
          <div>
            <div className="font-display text-lg text-umber">SeedTechNG · Chat console</div>
            <div className="text-xs text-umber/60">Signed in as {user?.email}</div>
          </div>
        </div>
        <button
          data-testid="admin-logout-button"
          onClick={handleLogout}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-umber hover:border-umber"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Sidebar */}
        <aside className="flex w-80 flex-col border-r border-border bg-white">
          <div className="border-b border-border px-4 py-3 text-xs uppercase tracking-wider text-umber/60">
            Conversations · {sessions.length}
          </div>
          <div className="flex-1 overflow-y-auto">
            {sessions.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-umber/50">
                <MessageSquare className="mx-auto mb-2 h-6 w-6 opacity-40" />
                No chats yet. They&apos;ll appear here as visitors message you.
              </div>
            )}
            {sessions.map((s) => (
              <button
                key={s.id}
                data-testid={`session-row-${s.id}`}
                onClick={() => setActiveId(s.id)}
                className={`flex w-full flex-col items-start gap-1 border-b border-border px-4 py-3 text-left transition ${
                  activeId === s.id ? "bg-sand" : "hover:bg-sand/50"
                }`}
              >
                <div className="flex w-full items-center justify-between">
                  <span className="font-medium text-umber">{s.visitor_name || "Guest"}</span>
                  {s.unread_admin > 0 && activeId !== s.id && (
                    <span className="rounded-full bg-terracotta px-2 py-0.5 text-[10px] font-medium text-cream">
                      {s.unread_admin}
                    </span>
                  )}
                </div>
                {s.visitor_email && <span className="text-[11px] text-umber/55">{s.visitor_email}</span>}
                <span className="line-clamp-1 text-xs text-umber/60">{s.last_message_preview || "(no messages yet)"}</span>
                <span className="text-[10px] uppercase tracking-wider text-umber/40">
                  {new Date(s.updated_at).toLocaleString()}
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* Chat panel */}
        <main className="flex min-w-0 flex-1 flex-col bg-sand/30">
          {!session ? (
            <div className="flex flex-1 items-center justify-center text-umber/50">
              Select a conversation to start replying.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b border-border bg-white px-6 py-4">
                <div>
                  <div className="font-display text-lg text-umber">{session.visitor_name}</div>
                  <div className="text-xs text-umber/55">{session.visitor_email || "no email"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="toggle-ai-button"
                    onClick={toggleAI}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                      session.ai_enabled
                        ? "border-moss/40 bg-moss/10 text-moss"
                        : "border-border bg-white text-umber/60"
                    }`}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    AI auto-reply: {session.ai_enabled ? "ON" : "OFF"}
                  </button>
                </div>
              </div>

              <div ref={scrollRef} className="chat-scroll flex-1 space-y-3 overflow-y-auto px-6 py-6">
                {messages.map((m) => (
                  <AdminMessage key={m.id} msg={m} />
                ))}
              </div>

              <form onSubmit={sendReply} className="flex items-end gap-3 border-t border-border bg-white px-6 py-4">
                <textarea
                  data-testid="admin-reply-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendReply();
                    }
                  }}
                  rows={2}
                  placeholder="Type your reply…  (Enter to send, Shift+Enter for new line)"
                  className="flex-1 resize-none rounded-md border border-border bg-white px-3 py-2 text-sm focus:border-umber focus:outline-none"
                />
                <button
                  data-testid="admin-reply-send"
                  type="submit"
                  disabled={sending || !text.trim()}
                  className="inline-flex items-center gap-2 rounded-md bg-terracotta px-4 py-2.5 text-sm font-medium text-cream transition hover:bg-terracotta-dark disabled:opacity-50"
                >
                  <Send className="h-4 w-4" /> Send
                </button>
              </form>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function AdminMessage({ msg }) {
  const isVisitor = msg.sender === "visitor";
  const isAI = msg.sender === "ai";

  if (isVisitor) {
    return (
      <div className="flex items-start gap-2">
        <div className="mt-1 flex h-7 w-7 items-center justify-center rounded-full bg-umber text-cream">
          <UserIcon className="h-3.5 w-3.5" />
        </div>
        <div className="max-w-[70%]">
          <div className="mb-0.5 text-[10px] uppercase tracking-wider text-umber/55">Visitor</div>
          <div className="rounded-2xl rounded-tl-sm border border-border bg-white px-3.5 py-2 text-sm text-umber">
            {msg.text}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-end">
      <div className="max-w-[70%]">
        <div
          className={`mb-0.5 text-right text-[10px] uppercase tracking-wider ${
            isAI ? "text-moss" : "text-terracotta"
          }`}
        >
          {isAI ? (
            <span className="inline-flex items-center gap-1">
              <Bot className="h-3 w-3" /> AI auto-reply
            </span>
          ) : (
            <span>You ({msg.sender_name || "Staff"})</span>
          )}
        </div>
        <div
          className={`rounded-2xl rounded-tr-sm px-3.5 py-2 text-sm whitespace-pre-wrap ${
            isAI ? "border border-moss/20 bg-moss/5 text-umber" : "bg-terracotta text-cream"
          }`}
        >
          {msg.text}
        </div>
      </div>
    </div>
  );
}
